/**
 * useTranslation Hook
 *
 * Access translations in any component.
 *
 * @example
 * const { t, language, setLanguage } = useTranslation();
 *
 * // Simple usage
 * <h1>{t('dashboard.title')}</h1>
 *
 * // With parameters
 * <p>{t('welcome.message', { name: 'John' })}</p>
 * // Locale: "welcome.message": "Hello, {name}!"
 * // Result: "Hello, John!"
 *
 * // Change language
 * <button onClick={() => setLanguage('en')}>English</button>
 */

import { useContext, useCallback } from 'react';
import { LanguageContext } from './LanguageContext';

/**
 * Get nested value from object by dot-notation key
 * @param {Object} obj
 * @param {string} path - e.g., 'dashboard.subscription.status'
 * @returns {*}
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Replace {param} placeholders with values
 * @param {string} template
 * @param {Object} params
 * @returns {string}
 */
function interpolate(template, params) {
  if (!params || typeof template !== 'string') {
    return template;
  }

  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return params[key] !== undefined ? params[key] : match;
  });
}

/**
 * Translation hook
 *
 * @returns {Object}
 * @property {(key: string, params?: Object) => string} t - Get translated string
 * @property {'ru' | 'en'} language - Current language code
 * @property {(lang: 'ru' | 'en') => void} setLanguage - Change language
 */
export function useTranslation() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }

  const { language, setLanguage, translations } = context;

  /**
   * Get translation by key
   * @param {string} key - Dot-notation key, e.g., 'dashboard.title'
   * @param {Object} [params] - Interpolation params, e.g., { name: 'John' }
   * @returns {string}
   */
  const t = useCallback((key, params) => {
    const value = getNestedValue(translations, key);

    if (value === undefined) {
      // Return key as fallback (helps identify missing translations)
      console.warn(`[i18n] Missing translation: ${key}`);
      return key;
    }

    return interpolate(value, params);
  }, [translations]);

  return {
    t,
    language,
    setLanguage
  };
}
